// Agent orbs — the one visual identity an agent carries across Studio.
//
// Shared by the list page's cards (a 1.75rem chip beside the name) and the web
// call dock's hero orb, so the thing you clicked and the thing you are now
// talking to are recognisably the same agent.
//
// The wash is a conic gradient rather than the two-stop linear one AVR-621
// shipped: at a single hue and pastel lightness the blobs were near enough to
// each other that they stopped working as a recognition aid, and the hero orb
// had nothing to animate. Wedges of deep → mid → light around the circle give
// each agent a distinct silhouette at chip size and something that reads as
// motion when it spins.
// Balto's brand family, per PIB-544: indigo/violet primary with teal and soft
// blue accents, every value a DS token — deliberately *not* ElevenLabs'
// teal-only look, which the earlier passes here drifted into.
//
//   deep  → blurple-500..800 / violet-500..700   (the primary, indigo or violet)
//   mid   → cyan-200..400 / blurple-300          (the teal or soft-blue accent)
//   light → the same hue's 50..200 tint
//
// Six variants rather than one, because the orb is also how you tell one agent
// from another on the list page (AVR-556). They stay inside the brand family:
// which primary leads and which accent answers it is what distinguishes them,
// not a walk around the colour wheel.
//
// `light` is a real tint, not a near-white. An earlier pass used #f2fdf8-type
// values and against a white page those wedges simply weren't there — the orb
// read as disconnected smudges rather than one turning object.
const ORB_PALETTES = [
  { key: 'indigo', light: '#d9e3ff', mid: '#72e8db', deep: '#3f47ec' },
  { key: 'violet', light: '#f2eafe', mid: '#95b0ff', deep: '#8a4de7' },
  { key: 'blurple', light: '#ebf0ff', mid: '#02d4be', deep: '#4b60f6' },
  { key: 'iris', light: '#e0ccfd', mid: '#72e8db', deep: '#7541c3' },
  { key: 'cobalt', light: '#ccf6ee', mid: '#95b0ff', deep: '#2b30a8' },
  { key: 'orchid', light: '#d9e3ff', mid: '#02b9a6', deep: '#a36afa' },
]

// Stable palette pick: sum the identity's char codes. Not a real hash — it only
// has to spread a handful of agents across six palettes and never change for a
// given agent, so the orb survives renders, reloads and org switches.
export const paletteFor = (identity) => {
  const key = String(identity ?? '')
  let total = 0
  for (let index = 0; index < key.length; index += 1) {
    total += key.charCodeAt(index)
  }
  return ORB_PALETTES[total % ORB_PALETTES.length]
}

// Uneven stops on purpose: evenly spaced ones read as a colour wheel, which is
// a chart, not an object. Two deep wedges of different widths, separated by
// light, give the blob a front and a back — so a spin is legible as rotation
// rather than as a flicker.
export const conicWash = (palette, spinDeg = 0) =>
  [
    `conic-gradient(from ${spinDeg}deg at 50% 50%,`,
    `${palette.light} 0deg,`,
    `${palette.deep} 48deg,`,
    `${palette.mid} 96deg,`,
    `${palette.light} 150deg,`,
    `${palette.mid} 196deg,`,
    `${palette.deep} 250deg,`,
    `${palette.mid} 300deg,`,
    `${palette.light} 360deg)`,
  ].join(' ')

// Hairline inset edge. At the light end of the wash a flat fill dissolves into
// the white card, and the mock's blobs carry initials to hold their shape where
// ours (by agreement on AVR-621) do not.
export const orbRing = (palette) => `inset 0 0 0 1px ${palette.deep}33`
